import type { LearningArticleDefinition, LearningStageId } from "@/types/learning";
import { JOURNAL_REWRITE_ARTICLES } from "@/utilities/journalRewriteArticles";
import { LearningArticleStatusMap } from "@/view_models/atoms";

import LearningAdvancedImage from "@assets/image/learning_image_advanced.png";
import LearningBeginnerImage from "@assets/image/learning_image_beginner.png";
import LearningIntermediateImage from "@assets/image/learning_image_intermediate.png";

export type { LearningArticleDefinition, LearningStageId } from "@/types/learning";

export type LearningStageDefinition = {
  id: LearningStageId;
  label: string;
  title: string;
  message: string;
  objective: string;
  buttonLabel: string;
  articles: LearningArticleDefinition[];
};

export const STAGE_IMAGE_MAP: Record<LearningStageId, string> = {
  beginner: LearningBeginnerImage,
  intermediate: LearningIntermediateImage,
  advanced: LearningAdvancedImage,
};

const STAGE_META: Record<LearningStageId, Omit<LearningStageDefinition, "articles">> = {
  beginner: {
    id: "beginner",
    label: "初級編",
    title: "基礎から学ぶ",
    message: "不動産投資の全体像や、\n基礎用語を理解しましょう",
    objective: "不動産投資の全体像とリスクを正しく理解し、投資家としての「判断軸（モノサシ）」となる基礎用語（特に利回り・融資）を習得する。",
    buttonLabel: "初級編を学ぶ",
  },
  intermediate: {
    id: "intermediate",
    label: "中級編",
    title: "実践で学ぶ",
    message: "融資・収支計算・運営管理など、\n実際に使うノウハウを学びましょう",
    objective: "データと現地調査に基づき、「買うべき物件」と「組むべきパートナー」を自ら選定するスキルを習得する。",
    buttonLabel: "中級編を学ぶ",
  },
  advanced: {
    id: "advanced",
    label: "上級編",
    title: "戦略を磨く",
    message: "法人化・ポートフォリオ戦略や\n出口戦略についてマスターしましょう",
    objective: "保有する物件の収益を最大化し、ポートフォリオ全体を最適化する。市況を見据えた出口戦略（売却）を立案・実行できるプロレベルの戦略を学ぶ。",
    buttonLabel: "上級編を学ぶ",
  },
};

const groupedArticles: Record<LearningStageId, LearningArticleDefinition[]> = {
  beginner: [],
  intermediate: [],
  advanced: [],
};

JOURNAL_REWRITE_ARTICLES.forEach((article) => {
  groupedArticles[article.stageId].push(article);
});

export const LEARNING_STAGES: LearningStageDefinition[] = (Object.keys(STAGE_META) as LearningStageId[]).map(
  (stageId) => ({
    ...STAGE_META[stageId],
    articles: groupedArticles[stageId],
  })
);

export const LEARNING_ARTICLES = [...JOURNAL_REWRITE_ARTICLES];

export const findStageById = (stageId: string | undefined): LearningStageDefinition | undefined =>
  LEARNING_STAGES.find((stage) => stage.id === stageId);

export const findArticleById = (articleId: string | undefined): LearningArticleDefinition | undefined =>
  LEARNING_ARTICLES.find((article) => article.id === articleId);

export const isStageCompleted = (
  stage: LearningStageDefinition,
  statusMap: LearningArticleStatusMap
): boolean => stage.articles.every((article) => Boolean(statusMap[article.id]));
